import React, { useState } from "react";
import {
  AppBar, Toolbar, Box, IconButton, Drawer, List, ListItemButton,
  ListItemIcon, ListItemText, Divider, Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import WorkOutlineIcon from "@mui/icons-material/WorkOutlineOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import FlightLandIcon from "@mui/icons-material/FlightLand";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutlineOutlined";
import { NavLink } from "react-router-dom";
import { BRAND, SECTION_COLORS } from "../theme";
import BrandMark from "./BrandMark";

const NAV_ITEMS = [
  { label: "Inicio",    to: "/",          icon: HomeOutlinedIcon,          accent: BRAND.nodoBlue },
  { label: "Economía",  to: "/economia",  icon: QueryStatsIcon,            accent: SECTION_COLORS.economia.main },
  { label: "Trabajo",   to: "/trabajo",   icon: WorkOutlineIcon,           accent: SECTION_COLORS.trabajo.main },
  { label: "Salud",     to: "/salud",     icon: LocalHospitalOutlinedIcon, accent: SECTION_COLORS.salud.main },
  { label: "Migración", to: "/migracion", icon: FlightLandIcon,            accent: SECTION_COLORS.migracion.main },
  { label: "Seguridad", to: "/seguridad", icon: ShieldOutlinedIcon,        accent: SECTION_COLORS.seguridad.main },
  { label: "Contacto",  to: "/contacto",  icon: MailOutlineIcon,           accent: BRAND.nodoBlue },
];

/* Franja institucional: azul Nodo + rojo y amarillo FES */
const FRANJA = `linear-gradient(90deg,
  ${BRAND.nodoBlue} 0%, ${BRAND.nodoBlue} 72%,
  ${BRAND.fesRed} 72%, ${BRAND.fesRed} 90%,
  ${BRAND.fesYellow} 90%, ${BRAND.fesYellow} 100%)`;

export default function Navbar() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: "rgba(255,255,255,0.86)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          borderBottom: `1px solid ${BRAND.border}`,
          zIndex: 1200,
        }}
      >
        <Toolbar
          sx={{
            maxWidth: 1440,
            width: "100%",
            mx: "auto",
            px: { xs: 2, md: 3 },
            py: 1.5,
            gap: 2,
            minHeight: "80px !important",
          }}
        >
          {/* Marca */}
          <Box
            component={NavLink}
            to="/"
            aria-label="Datos para la Democracia — Inicio"
            sx={{
              display: "flex",
              alignItems: "center",
              mr: 3,
              flexShrink: 0,
              textDecoration: "none",
            }}
          >
            <BrandMark height={48} />
          </Box>

          <Box sx={{ flex: 1 }} />

          {/* Menú desktop con subrayado animado */}
          <Box
            component="nav"
            aria-label="Navegación principal"
            sx={{ display: { xs: "none", md: "flex" }, gap: 3, alignItems: "center" }}
          >
            {NAV_ITEMS.map((item) => (
              <Box
                key={item.to}
                component={NavLink}
                to={item.to}
                end={item.to === "/"}
                sx={{
                  position: "relative",
                  py: 0.75,
                  fontSize: "0.845rem",
                  fontWeight: 600,
                  letterSpacing: 0.2,
                  color: BRAND.textSub,
                  whiteSpace: "nowrap",
                  textDecoration: "none",
                  transition: "color 0.18s ease",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: "2.5px",
                    borderRadius: 2,
                    backgroundColor: BRAND.nodoBlue,
                    transform: "scaleX(0)",
                    transformOrigin: "left center",
                    transition: "transform 0.22s ease",
                  },
                  "&:hover": { color: BRAND.text },
                  "&:hover::after": { transform: "scaleX(0.35)" },
                  "&.active": { color: BRAND.nodoBlue },
                  "&.active::after": { transform: "scaleX(1)" },
                }}
              >
                {item.label}
              </Box>
            ))}
          </Box>

          {/* Hamburguesa mobile */}
          <IconButton
            sx={{ display: { xs: "flex", md: "none" }, color: BRAND.nodoBlue }}
            onClick={() => setOpen(true)}
            aria-label="Abrir menú de navegación"
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>

        {/* Franja de identidad */}
        <Box aria-hidden sx={{ height: 3, background: FRANJA }} />
      </AppBar>

      {/* Drawer mobile */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { width: 296, borderRadius: "16px 0 0 16px" } }}
      >
        <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", height: "100%" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.3 }}>
              <BrandMark height={40} />
              <Typography sx={{ fontSize: "0.6rem", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: BRAND.textFaint }}>
                Nodo XXI · FES
              </Typography>
            </Box>
            <IconButton onClick={() => setOpen(false)} aria-label="Cerrar menú" size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          <List sx={{ flex: 1 }}>
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <ListItemButton
                  key={item.to}
                  component={NavLink}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setOpen(false)}
                  sx={{
                    borderRadius: 2.5,
                    mb: 0.5,
                    px: 1.5,
                    py: 1,
                    "&.active": {
                      bgcolor: `${item.accent}12`,
                      "& .MuiListItemText-primary": { color: item.accent, fontWeight: 700 },
                      "& .MuiListItemIcon-root": { color: item.accent },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 38, color: BRAND.textSub }}>
                    <Icon sx={{ fontSize: 21 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontWeight: 600, fontSize: "0.93rem" }}
                  />
                </ListItemButton>
              );
            })}
          </List>

          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, px: 0.5 }}>
            <Box component="img" src="/img/logoNodo_COLOR.png" alt="Fundación Nodo XXI" sx={{ height: 30, objectFit: "contain" }} />
            <Box component="img" src="/img/FES-Logo_Standard_Rot_Bildschirm.png" alt="FES Chile" sx={{ height: 26, objectFit: "contain" }} />
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
